import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { fromJS } from 'immutable'
import { CircularProgress, Grid, Tooltip } from '@mui/material'
import InfoIcon from '@mui/icons-material/Info'
import MuiButton from 'gui/components/common/MuiButton'
import { selectAuth } from 'services/auth/redux/selectors'
import { showErrorToast } from 'services/toast/redux/actions'
import { selectBrooklynServerUrl } from 'services/config/redux/selectors'
import { createImage, deleteImage } from 'services/images'
import { getD6ContentType } from 'services/content/d6-content-types'
import type { Image } from '@/__generated__/gql/graphql'
import './content-images-upload-field.scss'

interface ContentImagesUploadFieldProps {
  type: string
  title: string
  contentId: number
  contentType: string
  language: string
  primaryLanguage: string
  image?: Image | null
  onImageCreated?: () => void
  onImageDeleted?: () => void
}

export default function ContentImagesUploadField (props: ContentImagesUploadFieldProps) {
  const {
    contentId,
    contentType,
    language,
    primaryLanguage,
    type,
    title,
    image,
    onImageCreated,
    onImageDeleted,
  } = props
  const dispatch = useDispatch()
  const auth = useSelector(selectAuth)
  const { url: brooklynUrl } = useSelector(selectBrooklynServerUrl)

  const [isLoading, setIsLoading] = useState(false)
  const [imageDimensions, setImageDimensions] = useState<{ width: number; height: number } | null>(null)
  const [fileSize, setFileSize] = useState<number | null>(null)
  const imageRef = useRef<HTMLImageElement>(null)

  // Format file size in human-readable format
  const formatFileSize = useCallback((bytes: number | null): string => {
    if (bytes === null || bytes === 0) return 'Unknown'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return `${parseFloat((bytes / k**i).toFixed(2))} ${sizes[i]}`
  }, [])

  // Handle image load to capture dimensions and file size
  const handleImageLoad = useCallback(async () => {
    if (!imageRef.current || !image?.url) return

    const img = imageRef.current
    // Get dimensions from naturalWidth/naturalHeight
    if (img.naturalWidth && img.naturalHeight) {
      setImageDimensions({
        width: img.naturalWidth,
        height: img.naturalHeight,
      })
    }

    // Fetch file size from HTTP headers
    try {
      const response = await fetch(image.url, { method: 'HEAD' })
      const contentLength = response.headers.get('Content-Length')
      if (contentLength) {
        setFileSize(parseInt(contentLength, 10))
      }
    } catch {
      // If HEAD fails, try GET but only read headers
      try {
        const response = await fetch(image.url, { method: 'GET' })
        const contentLength = response.headers.get('Content-Length')
        if (contentLength) {
          setFileSize(parseInt(contentLength, 10))
        }
      } catch {
        // Silently fail - file size will remain null
      }
    }
  }, [image?.url])

  // Reset metadata when image changes and trigger load if already cached
  useEffect(() => {
    if (!image?.url) {
      setImageDimensions(null)
      setFileSize(null)
      return
    }

    // If image is already loaded (cached), trigger the load handler
    if (imageRef.current?.complete) {
      handleImageLoad()
    }
  }, [image?.url, handleImageLoad])

  // Create content relationship object
  const contentRelationship = useMemo(
    () => ({
      d6Id: contentId,
      d6ContentType: getD6ContentType(contentType),
      language,
      isSource: language === primaryLanguage,
      type,
    }),
    [type, contentId, contentType, language, primaryLanguage],
  )

  // Handle image upload
  const handleImageUpload = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      e.preventDefault()
      const file = e.target.files?.[0]
      if (file) {
        setIsLoading(true)
        const fileReader = new FileReader()
        fileReader.onload = async (fileEvent) => {
          const dataUri = fileEvent.target?.result ?? null
          const imageData = fromJS({
            contentRelationship,
            originalFileName: file.name,
            url: dataUri,
          })

          try {
            await createImage({
              image: imageData,
              auth,
              server: brooklynUrl,
              processImage: true,
            })
            onImageCreated?.()
          } catch (error: unknown) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error'
            dispatch(showErrorToast(`Could not create image: ${errorMessage}`))
          } finally {
            setIsLoading(false)
          }
        }
        fileReader.onerror = (error: unknown) => {
          const errorMessage = error instanceof Error ? error.message : 'Unknown error'
          dispatch(showErrorToast(`Could not read file: ${errorMessage}`))
          setIsLoading(false)
        }
        fileReader.readAsDataURL(file)
      }
    },
    [auth, brooklynUrl, contentRelationship, onImageCreated, dispatch],
  )

  // Handle delete image
  const handleDelete = useCallback(
    async (e: React.MouseEvent) => {
      e.preventDefault()
      // eslint-disable-next-line no-restricted-globals
      if (!confirm('This will remove this image from the database.')) {
        return
      }
      if (!image?.id) {
        return
      }
      setIsLoading(true)
      try {
        await deleteImage({
          auth,
          server: brooklynUrl,
          image: fromJS({ id: image.id }),
        })
        onImageDeleted?.()
      } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error'
        dispatch(showErrorToast(`Could not delete image: ${errorMessage}`))
      } finally {
        setIsLoading(false)
      }
    },
    [image, auth, brooklynUrl, onImageDeleted, dispatch],
  )

  // Extract filename from image or URL
  const getFileName = useCallback((): string => {
    if (image?.originalFileName) {
      return image.originalFileName
    }
    if (image?.url) {
      try {
        const url = new URL(image.url)
        const {pathname} = url
        const filename = pathname.split('/').pop() || pathname
        return filename || 'Unknown'
      } catch {
        return 'Unknown'
      }
    }
    return 'Unknown'
  }, [image?.originalFileName, image?.url])

  const tooltipContent = useMemo(() => {
    if (!image?.url) return null

    return (
      <>
        {imageDimensions ? (
          <>
            Width: {imageDimensions.width}px<br />
            Height: {imageDimensions.height}px<br />
          </>
        ) : (
          <>
            Width: Loading...<br />
            Height: Loading...<br />
          </>
        )}
        {fileSize !== null ? (
          <>Size: {formatFileSize(fileSize)}<br /></>
        ) : (
          <>Size: Loading...<br /></>
        )}
        File name: {getFileName()}
      </>
    )
  }, [image?.url, imageDimensions, fileSize, formatFileSize, getFileName])

  return (
    <Grid item xs={12} md={6}>
      <p style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: 0 }}>
        <span>
          {title} ({type})
        </span>
        {image?.url && (
          <Tooltip title={tooltipContent || 'Loading image metadata...'} arrow>
            <InfoIcon fontSize="small" sx={{ color: 'text.secondary', cursor: 'help' }} />
          </Tooltip>
        )}
      </p>
      {image?.url ? (
        <>
          <div className="content-images-upload-field">
            <div className="content-images-upload-field__image-container">
              <img
                ref={imageRef}
                className="content-images-upload-field__image"
                src={image.url}
                alt={title}
                onLoad={handleImageLoad}
              />
            </div>
          </div>
          <MuiButton variant="contained" color="error" onClick={handleDelete} disabled={isLoading}>
            Delete Image
          </MuiButton>
        </>
      ) : (
        <div className="content-images-upload-field">
          <input
            className="content-images-upload-field__input"
            type="file"
            id={`content-images-${type}`}
            onChange={handleImageUpload}
            disabled={isLoading}
          />
          <label htmlFor={`content-images-${type}`} className="content-images-upload-field__label">
            {isLoading ? (
              <CircularProgress size={60} sx={{ color: '#3a88fc' }} />
            ) : (
              'Click To Select Or Drag Image Here'
            )}
          </label>
        </div>
      )}
    </Grid>
  )
}
